import { ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useIsDesktop } from "../../pages/stockList/checkoutV2/lib/useIsDesktop";
import { NavigationBar } from "../navigation/NavigationBar";
import { useFocusedNavigation } from "../../hooks/useFocusedNavigation";
import { EntityAssignmentWizard } from "./EntityAssignmentWizard";
import { AssignedToCustomers } from "./AssignedToCustomers";
import { pluralize } from "@sellar-io/common";
import { Alert } from "../alert/Alert";
import {
  AssignedCustomersConnectionType,
  AssignedCustomersDialog,
} from "./AssignedCustomersDialog";
import { Button, Card, IconType } from "@sellar-io/components";
import { ConvertDiscountsWarningDialog } from "./ConvertDiscountsWarningDialog";
import { useSupplierRelationshipList } from "../../hooks/api/v2/supplierRelationship/useSupplierRelationshipList";
import { AllFulfillmentTypesType } from "@sellar-io/api-v2/types";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { TwoPanelLayout } from "../dashboard/TwoPanelLayout";

type TradeTermsLayoutProps = {
  title: string;
  children: ReactNode;
  aside: ReactNode;
  onClickLeftIcon?: () => void;
  type?:
    | "product catalogue"
    | "price list"
    | "delivery method"
    | "volume discount";
  fulfillmentType?: Extract<
    AllFulfillmentTypesType,
    "haulier" | "direct" | "courier" | "sellarDistribution"
  >;
  icon?: IconType;
  detailTitle?: string;
  isNew?: boolean;
  existingConnections?: AssignedCustomersConnectionType[];
  existingCustomerZoneIds?: number[];
  onAssign?: ({
    connectionIds,
    customerZoneIds,
  }: {
    connectionIds: number[];
    customerZoneIds: number[];
  }) => Promise<{
    success: boolean;
    message?: string;
  }>;
  showAssignment?: boolean;
  hideCustomerZones?: boolean;
  assignmentHeader?: ReactNode;
  hideFooter?: boolean;
  hideAssignButton?: boolean;
  saving?: boolean;
  additionalAssignmentText?: string;
};

export const TradeTermsLayout = ({
  title,
  children,
  aside,
  onClickLeftIcon,
  type,
  fulfillmentType,
  icon,
  detailTitle,
  isNew,
  existingConnections,
  existingCustomerZoneIds,
  onAssign,
  showAssignment: _showAssignment = false,
  hideCustomerZones = false,
  assignmentHeader,
  hideFooter,
  hideAssignButton = false,
  saving,
  additionalAssignmentText,
}: TradeTermsLayoutProps) => {
  const { isDesktop } = useIsDesktop();
  useFocusedNavigation();

  const unbundling4cEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  const [showAssignment, setShowAssignment] = useState(_showAssignment);
  const [showAssignedCustomers, setShowAssignedCustomers] = useState(false);
  const [showConvertDiscounts, setShowConvertDiscounts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connectionIds, setConnectionIds] = useState<number[]>([]);
  const [customerZoneIds, setCustomerZoneIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data: supplierRelationshipListData } = useSupplierRelationshipList(
    {
      query: [
        {
          type: "hasCustomerLevelDiscounts",
          modifier: "EQUALS",
          value: true,
        },
        { type: "connectionIds", modifier: "IN", value: connectionIds },
      ],
      limit: 50,
    },
    {
      enabled: type === "price list" && connectionIds.length > 0,
    },
  );

  const connectionsWithDiscounts =
    supplierRelationshipListData?.pages.flat() ?? [];

  const hasConnectionsWithDiscounts = connectionsWithDiscounts.length > 0;

  const mainNavigationBar = (
    <NavigationBar
      title={title}
      leftIcon={!isDesktop ? (icon ?? "CLOSE") : undefined}
      onClickLeftIcon={onClickLeftIcon}
      leftIconText={!isDesktop && saving ? "Saving..." : undefined}
    />
  );

  const detailNavigationBar = (
    <NavigationBar
      title={detailTitle ?? "Details"}
      leftIcon={icon ?? "CLOSE"}
      onClickLeftIcon={onClickLeftIcon}
      leftIconText={saving ? "Saving..." : undefined}
    />
  );

  const footer = (
    <div
      className={twMerge(
        "fixed bottom-0 flex flex-col gap-4 border-t border-t-zinc-300 bg-white p-4",
        isDesktop ? "w-[calc(100%-400px)]" : "w-full",
      )}
    >
      <AssignedToCustomers
        numberOfCustomers={existingConnections?.length ?? 0}
        numberOfZones={
          unbundling4cEnabled && type === "delivery method"
            ? existingCustomerZoneIds?.length
            : undefined
        }
        onClick={() => !isNew && setShowAssignedCustomers(true)}
        additionalText={additionalAssignmentText}
      />
      {!isNew && !hideAssignButton && (
        <Button className="w-full" onClick={() => setShowAssignment(true)}>
          Assign
        </Button>
      )}
    </div>
  );

  const wizard = (
    <>
      <EntityAssignmentWizard
        currentListName={title}
        connectionIds={connectionIds}
        customerZoneIds={customerZoneIds}
        setConnectionIds={setConnectionIds}
        setCustomerZoneIds={setCustomerZoneIds}
        existingConnectionIds={existingConnections?.map((c) => c.id)}
        existingCustomerZoneIds={existingCustomerZoneIds}
        header={assignmentHeader}
        type={type}
        fulfillmentType={fulfillmentType}
        hideCustomerZones={hideCustomerZones}
      />
      <div
        className={twMerge(
          "fixed bottom-0 flex flex-col gap-4 border-t border-t-zinc-300 bg-white p-4",
          isDesktop ? "w-[calc(100%-400px)]" : "w-full",
        )}
      >
        {connectionIds.length > 0 && (
          <Alert
            type="information"
            title={`${connectionIds.length} ${pluralize(
              "customer",
              connectionIds.length,
            )} will be affected by this change`}
            message={`When you click Assign, ${connectionIds.length} ${pluralize(
              "customer",
              connectionIds.length,
            )}'s ${type} will change to '${title}'`}
            className="mr-0 w-full"
          />
        )}
        {customerZoneIds.length > 0 && (
          <Alert
            type="information"
            title={`${customerZoneIds.length} ${pluralize(
              "customer zone",
              customerZoneIds.length,
            )} will be affected by this change`}
            message={`When you click Assign, ${customerZoneIds.length} ${pluralize(
              "customer zone",
              customerZoneIds.length,
            )} ${type} will change to '${title}'`}
            className="mr-0 w-full"
          />
        )}
        {error && (
          <Alert
            type="error"
            title="Something went wrong"
            message={error}
            className="mr-0 w-full"
          />
        )}
        <div className="flex w-full gap-2">
          <Button
            className="w-full"
            color="secondary"
            onClick={() => setShowAssignment(false)}
          >
            Back
          </Button>
          <Button
            className="w-full"
            onClick={() => {
              if (onAssign) {
                if (hasConnectionsWithDiscounts) {
                  setShowConvertDiscounts(true);
                } else {
                  setLoading(true);
                  onAssign({ connectionIds, customerZoneIds }).then(
                    (result) => {
                      setLoading(false);
                      setConnectionIds([]);
                      setCustomerZoneIds([]);
                      if (result.success) {
                        setShowAssignment(false);
                      } else {
                        setError(result.message || "An error occurred");
                      }
                    },
                  );
                }
              }
            }}
            loading={loading}
            disabled={
              connectionIds.length === 0 && customerZoneIds.length === 0
            }
          >
            Assign
          </Button>
        </div>
      </div>
    </>
  );

  if (!isDesktop) {
    return (
      <>
        {mainNavigationBar}
        <div className="flex min-h-[calc(100vh-var(--nav-height))] flex-1 flex-col bg-zinc-100">
          <div className="mx-auto w-full max-w-[920px] p-4">
            <Card className="border border-zinc-300 bg-white">{aside}</Card>
          </div>
          {showAssignment ? (
            wizard
          ) : (
            <>
              {children}
              {!hideFooter && footer}
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <TwoPanelLayout
        firstPart="400px"
        secondPart="1fr"
        firstClassName="border-r border-r-zinc-300 bg-white"
        first={
          <div className="flex min-h-full flex-col">
            {detailNavigationBar}
            <div className="flex flex-1 flex-col">{aside}</div>
          </div>
        }
        second={
          <div className="flex min-h-full flex-col">
            {mainNavigationBar}
            <div className="flex flex-1 bg-zinc-100 pt-4">
              <div className={twMerge("flex w-full flex-col gap-2")}>
                <div className="h-full">
                  {showAssignment ? (
                    wizard
                  ) : (
                    <>
                      {children}
                      {!hideFooter && footer}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        }
      />
      <AssignedCustomersDialog
        active={showAssignedCustomers}
        onDismiss={() => setShowAssignedCustomers(false)}
        connections={existingConnections ?? []}
        termName={title}
        type={type}
      />
      {hasConnectionsWithDiscounts && (
        <ConvertDiscountsWarningDialog
          active={showConvertDiscounts}
          onDismiss={() => setShowConvertDiscounts(false)}
          onConfirm={() => {
            if (onAssign) {
              setLoading(true);
              onAssign({ connectionIds, customerZoneIds }).then((result) => {
                setLoading(false);
                setConnectionIds([]);
                setCustomerZoneIds([]);
                if (result.success) {
                  setShowAssignment(false);
                  setShowConvertDiscounts(false);
                } else {
                  setError(result.message || "An error occurred");
                }
              });
            }
          }}
          connections={connectionsWithDiscounts}
        />
      )}
    </>
  );
};
